import React, { useCallback, useEffect, useMemo, useState } from "react";
import styles from "../DocumentsViewer/DocumentsViewer.module.css";
import { calculateNpsScore } from "../../../utils/calculateNpsScore";
import { useGetConceptsFiltersMutation } from "../../../store/api/lumi/concepts";
import { getConceptFilter } from "../../../utils/getConceptFilter";
import { Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { debounce } from "lodash";
import PropTypes from "prop-types";

const NpsScoreWidget = ({ id, filters, selectedFieldDriverName, driverInterval }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [npsScore, setNpsScore] = useState(null);
    const [error, setError] = useState(null);

    const [getData] = useGetConceptsFiltersMutation();

    const conceptFilter = useMemo(
        () =>
            JSON.stringify(
                getConceptFilter({ id, filters, selectedFieldDriverName, driverInterval, selectedPageSize: 100 }),
            ),
        [id, filters, selectedFieldDriverName, driverInterval],
    );

    const getConceptData = useCallback(async () => {
        try {
            setIsLoading(true);
            const { data: dataConcepts } = await getData({
                id,
                concept_selector: JSON.parse(conceptFilter),
                filter: filters,
                breakdowns: [{ name: selectedFieldDriverName, interval: driverInterval }],
            });

            const breakdownData = dataConcepts?.breakdowns?.[0]?.buckets || [];
            if (breakdownData.length === 0) {
                setNpsScore(null);
                setIsLoading(false);
                return;
            }

            const npsData = breakdownData.map((concept) => ({
                value: concept.label,
                count: concept.filter_count,
            }));

            const calculatedScore = calculateNpsScore(npsData);
            setNpsScore(calculatedScore);
            setError(null);
            setIsLoading(false);
        } catch (e) {
            console.error("Error fetching concept data:", e);
            setError("Failed to fetch data.");
            setIsLoading(false);
        }
    }, [filters, selectedFieldDriverName, driverInterval, conceptFilter]);

    useEffect(() => {
        setIsLoading(true)
        const debouncedGetData = debounce(getConceptData, 1000);
        debouncedGetData();
        return () => {
            debouncedGetData.cancel();
        };
    }, [getConceptData]);

    if (isLoading) {
        return <Spin className="spinner-by-center" indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />;
    }

    if (error) {
        return <p>{error}</p>;
    }

    return (
        <div className={styles.documentsViewerContainer}>
            <div className={styles.documentsViewerInner}>
                {npsScore !== null ? (
                    <div className={styles.docCount}>{npsScore}</div>
                ) : (
                    <p>Field range too small</p>
                )}
            </div>
        </div>
    );
};

NpsScoreWidget.propTypes = {
    id: PropTypes.string.isRequired,
    filters: PropTypes.array,
    selectedFieldDriverName: PropTypes.string.isRequired,
    driverInterval: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export default NpsScoreWidget;
